import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useCategory } from '../store/slice/categorySlice';
import AdvancedFilter from '../components/AdvancedFilter';
import { ActionButton, ActionButtonGroup } from '../components/ActionControls';
import { isCategoryActive, categoryStatusLabel } from '../utils/categoryHelpers';

function CategoriesPage() {
  const { categories, loading, error, fetchCategories, deleteCategory } = useCategory();
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    sortBy: '',
  });

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const filterConfig = [
    { key: 'search', type: 'search', value: filters.search },
    {
      key: 'status',
      type: 'select',
      label: 'Status',
      value: filters.status,
      defaultValue: 'all',
      options: [
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
      ],
    },
    {
      key: 'sortBy',
      type: 'sort',
      label: 'Sort By',
      value: filters.sortBy,
      defaultValue: '',
      options: [
        { value: 'name-asc', label: 'Name (A-Z)' },
        { value: 'name-desc', label: 'Name (Z-A)' },
        { value: 'date-desc', label: 'Newest first' },
        { value: 'date-asc', label: 'Oldest first' },
      ],
    },
  ];

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.status && filters.status !== 'all') count++;
    if (filters.sortBy) count++;
    return count;
  }, [filters]);

  const filteredCategories = useMemo(() => {
    let result = [...categories];

    if (filters.search) {
      const term = filters.search.toLowerCase();
      result = result.filter(
        (c) =>
          (c.name || '').toLowerCase().includes(term) ||
          (c.description || '').toLowerCase().includes(term)
      );
    }
    if (filters.status && filters.status !== 'all') {
      result = result.filter((c) => categoryStatusLabel(c) === filters.status);
    }
    if (filters.sortBy) {
      const [field, order] = filters.sortBy.split('-');
      result.sort((a, b) => {
        let va = field === 'date' ? new Date(a.createdAt || a.created_at || 0) : (a.name || '').toLowerCase();
        let vb = field === 'date' ? new Date(b.createdAt || b.created_at || 0) : (b.name || '').toLowerCase();
        if (order === 'desc') [va, vb] = [vb, va];
        return va < vb ? -1 : va > vb ? 1 : 0;
      });
    }

    return result;
  }, [categories, filters]);

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      const result = await deleteCategory(id);
      if (result?.meta?.requestStatus === 'fulfilled') {
        toast.success('Category deleted successfully');
      }
    }
  };

  if (loading && categories.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <p className="text-slate-500">Loading categories...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl text-slate-800 tracking-tight">Categories</h1>
          <p className="mt-1 text-slate-500">Manage service categories (e.g., Hair, Nails, Skincare)</p>
        </div>
        <Link
          to="/categories/add"
          className="btn-primary inline-flex items-center gap-2"
        >
          <span>+</span> Add Category
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Total Categories</p>
          <p className="mt-1 text-2xl font-bold text-slate-800">{categories.length}</p>
        </div>
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Filtered</p>
          <p className="mt-1 text-2xl font-bold text-amber-600">{filteredCategories.length}</p>
        </div>
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Active</p>
          <p className="mt-1 text-2xl font-bold text-emerald-600">
            {categories.filter((c) => isCategoryActive(c)).length}
          </p>
        </div>
      </div>

      <AdvancedFilter
        searchPlaceholder="Search by name or description..."
        filters={filterConfig}
        onFilterChange={handleFilterChange}
        activeCount={activeFilterCount}
      />

      <div className="card-premium overflow-hidden">
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/80">
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Name</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Description</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCategories.map((category) => (
                <tr key={category.id} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors">
                  <td className="py-4 px-6 font-medium text-slate-800">{category.name}</td>
                  <td className="py-4 px-6 text-slate-600 max-w-xs truncate" title={category.description}>
                    {category.description || '-'}
                  </td>
                  <td className="py-4 px-6">
                    <span
                      className={`status-pill ${
                        isCategoryActive(category) ? 'status-pill-success' : 'status-pill-muted'
                      }`}
                    >
                      {categoryStatusLabel(category)}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-right">
                    <ActionButtonGroup>
                      <ActionButton
                        type="button"
                        variant="danger"
                        onClick={() => handleDelete(category.id, category.name)}
                        disabled={loading}
                      >
                        Delete
                      </ActionButton>
                    </ActionButtonGroup>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredCategories.length === 0 && (
          <div className="py-16 text-center text-slate-500">
            No categories found. <Link to="/categories/add" className="text-amber-600 hover:text-amber-700 font-medium">Add one</Link> to get started!
          </div>
        )}
      </div>
    </div>
  );
}

export default CategoriesPage;
